import React, { useMemo } from 'react'
import { ChallengeService } from '@duck/api-definitions'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { FunnelIcon } from '@heroicons/react/24/outline'
import {
  getStatusBadgeColor,
  formatChallengeStatus,
  getFeedbackStatusBadgeColor,
  formatFeedbackGrade,
  statusFilterOptions,
  feedbackStatusFilterOptions,
  getFeedbackStatusFilterBadgeColor,
  weaponOfChoiceFilterOptions,
  getWeaponOfChoiceBadgeColor,
  formatWeaponOfChoice,
  getFeedbackReleaseState,
  type Column,
  type ChallengeColumnKey,
  type FeedbackReleaseState,
} from './adminChallenges.utils'
import type { TranslationKey } from '@/modules/i18n/types'
import { formatWorkTime } from '@/utils/time.utils'
import { RelativeDateTime } from '../datetime/RelativeDateTime'
import { useTar } from '@/modules'
import {
  AsyncMultiPick,
  CopyableId,
  type MultiPickOption,
} from '@duck/ui'
import { useLoadMoreOnScroll } from '@/hooks/useLoadMoreOnScroll'
import { renderSortIcon } from '@/hooks/TableSettings/generalTable.utils'
import type { EnumFilterOption } from '@/hooks/TableSettings/generalTable.utils'
import {
  TextFilterPopover,
  EnumFilterDropdown,
  ColumnVisibilityDropdown,
} from '@/hooks/TableSettings/tableSettings.components'
import { FilterSetManager } from '@/hooks/TableSettings/FilterSetManager'
import type { SortDirection, TableSortState } from '@/hooks/TableSettings/useTableSettings'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { useNavigate } from '@tanstack/react-router'
import { UserBadge } from '@/components/UserBadge'

interface ChallengesTableFilterApi {
  getColumnFilter: (columnKey: string) => string
  setColumnFilter: (columnKey: string, value: string) => void
  getColumnFilterArray: (columnKey: string) => string[]
  setColumnFilterArray: (columnKey: string, value: string[]) => void
  toggleColumnFilterArrayItem: (columnKey: string, item: string) => void
  clearColumnFilter: (columnKey: string) => void
}

/** Map of async-search filter keys to their fetch functions. */
interface AsyncSearchFetchers {
  [filterKey: string]: (query?: string) => Promise<MultiPickOption[]>
}

interface ChallengesTableProps {
  challenges: ChallengeService.ChallengeInfo[]
  totalChallenges: number
  visibleColumns: Column[]
  sortField: keyof ChallengeService.Challenge
  sortDirection: 'asc' | 'desc'
  onSort: (field: keyof ChallengeService.Challenge) => void
  loadingMode?: boolean
  hasMore?: boolean
  onLoadMore?: () => void
  isPageLoading?: boolean
  filterApi?: ChallengesTableFilterApi
  /** Fetch functions for async-search filter columns, keyed by filterKey. */
  asyncSearchFetchers?: AsyncSearchFetchers
  hasNoFilterResults?: boolean
  onClearFilters?: () => void
  /** When provided, Clear all + Column visibility are shown in the table header */
  tableSettings?: {
    hasActiveSettings: () => boolean
    clearAllSettings: () => void
    isColumnVisible: (key: string) => boolean
    toggleColumnVisibility: (key: string) => void
    availableColumns: Array<{ key: string; label: string }>
    /** Filter set manager props */
    tableId: string
    sortColumn: string | null
    sortDirection: SortDirection
    filters: Record<string, string | string[]>
    visibleColumns: Record<string, boolean>
    defaultVisibleColumns: Record<string, boolean>
    filterLabels: Record<string, Record<string, string>>
    setSort: (columnKey: string | null, direction: SortDirection) => void
    clearColumnFilter: (columnKey: string) => void
    applyFilterSet: (sort: TableSortState, filters: Record<string, string | string[]>, visibleColumns?: Record<string, boolean>, filterLabels?: Record<string, Record<string, string>>) => void
  }
}

export const ChallengesTable: React.FC<ChallengesTableProps> = ({
  challenges,
  totalChallenges,
  visibleColumns,
  sortField,
  sortDirection,
  onSort,
  loadingMode = false,
  hasMore = false,
  onLoadMore,
  isPageLoading = false,
  filterApi,
  asyncSearchFetchers,
  hasNoFilterResults = false,
  onClearFilters,
  tableSettings,
}) => {
  const { t } = useTar()
  const navigate = useNavigate()

  const getEnumOptionsForColumn = useMemo((): ((filterKey: string) => EnumFilterOption[] | undefined) => {
    return (filterKey: string) => {
      if (filterKey === 'status') {
        return statusFilterOptions.map((o) => ({
          value: o.value,
          label: o.label,
          component: (
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(o.value as ChallengeService.ChallengeStatusEnum)}`}
            >
              {t(o.label)}
            </span>
          ),
        }))
      }
      if (filterKey === 'feedbackStatus') {
        return feedbackStatusFilterOptions.map((o) => ({
          value: o.value,
          label: o.label,
          component: (
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getFeedbackStatusFilterBadgeColor(o.value)}`}
            >
              {t(o.label)}
            </span>
          ),
        }))
      }
      if (filterKey === 'weaponOfChoice') {
        return weaponOfChoiceFilterOptions.map((o) => ({
          value: o.value,
          label: o.label,
          component: (
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getWeaponOfChoiceBadgeColor(o.value as ChallengeService.TaskWeaponsOfChoiceEnum)}`}
            >
              {t(o.label)}
            </span>
          ),
        }))
      }
      return undefined
    }
  }, [t])

  const { sentinelRef: loadMoreSentinelRef, sentinelIndex: loadMoreSentinelIndex } = useLoadMoreOnScroll<HTMLTableRowElement>({
    hasMore,
    onLoadMore,
    isLoading: isPageLoading,
    disabled: loadingMode,
    itemCount: challenges.length,
    offsetFromEnd: 10,
  })

  const cellRenderers: Partial<Record<ChallengeColumnKey, (challengeInfo: ChallengeService.ChallengeInfo) => React.ReactNode>> = {
    id: (challengeInfo) => {
      const id = challengeInfo.challenge.id
      return (
        <CopyableId
          value={id}
          className="bg-muted/20 hover:bg-muted/40 hover:text-primary rounded px-1.5 py-0.5"
        />
      )
    },
    slug: (challengeInfo) => (
      <span className="text-xs font-mono text-muted-foreground">
        {challengeInfo.challenge.slug}
      </span>
    ),
    userId: (challengeInfo) => (
      <span onClick={(e) => e.stopPropagation()} className="inline-flex">
        <UserBadge
          userId={challengeInfo.challenge.userId}
          fullName={challengeInfo.user?.fullName}
          role={challengeInfo.user?.role}
        />
      </span>
    ),
    taskId: (challengeInfo) => (
      <span className="text-sm text-foreground">
        {challengeInfo.task?.name || challengeInfo.challenge.taskId}
      </span>
    ),
    status: (challengeInfo) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(challengeInfo.challenge.status)}`}>
        {t(formatChallengeStatus(challengeInfo.challenge.status))}
      </span>
    ),
    feedbackStatus: (challengeInfo) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getFeedbackStatusBadgeColor(challengeInfo.challenge.feedbackStatus)}`}>
        {challengeInfo.challenge.feedbackStatus ? challengeInfo.challenge.feedbackStatus.charAt(0).toUpperCase() + challengeInfo.challenge.feedbackStatus.slice(1) : 'Unprocessed'}
      </span>
    ),
    workTime: (challengeInfo) => (
      <span className="text-sm text-foreground">
        {formatWorkTime(challengeInfo.challenge.workTime, t)}
      </span>
    ),
    feedbackGrade: (challengeInfo) => (
      <span className="text-sm text-foreground">
        {formatFeedbackGrade(challengeInfo.challenge.feedbackGrade)}
      </span>
    ),
    feedbackReleased: (challengeInfo) => {
      const state = getFeedbackReleaseState(challengeInfo.challenge)
      const config: Record<FeedbackReleaseState, { label: TranslationKey; className: string }> = {
        natural: { label: 'challenges.naturallyReleased', className: 'bg-accent/10 text-accent border border-accent/20' },
        manual: { label: 'challenges.manuallyReleased', className: 'bg-success/10 text-success border border-success/20' },
        none: { label: 'challenges.notReleased', className: 'bg-muted/10 text-muted border border-muted/20' },
      }
      const { label, className } = config[state]
      return (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${className}`}>
          {t(label)}
        </span>
      )
    },
    weaponOfChoice: (challengeInfo) => {
      const weapon = challengeInfo.challenge.workState?.chosenWeapon
      if (!weapon) {
        return (
          <span className="text-sm text-muted-foreground">
            {t('common.notApplicable')}
          </span>
        )
      }
      return (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getWeaponOfChoiceBadgeColor(weapon)}`}>
          {t(formatWeaponOfChoice(weapon))}
        </span>
      )
    },
    startTime: (challengeInfo) => (
      <RelativeDateTime
        isoDateString={challengeInfo.challenge.startTime}
        className="text-sm text-muted-foreground"
      />
    ),
    endTime: (challengeInfo) => (
      challengeInfo.challenge.endTime ? (
        <RelativeDateTime
          isoDateString={challengeInfo.challenge.endTime}
          className="text-sm text-muted-foreground"
        />
      ) : (
        <span className="text-sm text-muted-foreground">
          {t('common.notApplicable')}
        </span>
      )
    ),
    lastWorkTimeUpdate: (challengeInfo) => (
      challengeInfo.challenge.lastWorkTimeUpdate ? (
        <RelativeDateTime
          isoDateString={challengeInfo.challenge.lastWorkTimeUpdate}
          className="text-sm text-muted-foreground"
        />
      ) : (
        <span className="text-sm text-muted-foreground">
          {t('common.notApplicable')}
        </span>
      )
    ),
    workState: (challengeInfo) => {
      if (!challengeInfo.challenge.workState) {
        return (
          <span className="text-sm text-muted-foreground">
            {t('common.notApplicable')}
          </span>
        )
      }

      const { solution, lastQuery } = challengeInfo.challenge.workState
      return (
        <div className="text-sm text-foreground space-y-1">
          {solution && (
            <div className="truncate max-w-xs" title={solution}>
              <span className="text-xs text-muted-foreground">Solution:</span>
              <div className="truncate">{solution}</div>
            </div>
          )}
          {lastQuery && (
            <div className="truncate max-w-xs" title={lastQuery}>
              <span className="text-xs text-muted-foreground">Last Query:</span>
              <div className="truncate font-mono text-xs">{lastQuery}</div>
            </div>
          )}
          {!solution && !lastQuery && (
            <span className="text-sm text-muted-foreground">
              {t('common.notApplicable')}
            </span>
          )}
        </div>
      )
    },
    feedbackText: (challengeInfo) => {
      if (!challengeInfo.challenge.feedbackText) {
        return (
          <span className="text-sm text-muted-foreground">
            {t('common.notApplicable')}
          </span>
        )
      }

      return (
        <div className="text-sm text-foreground max-w-xs">
          <div
            className="truncate"
            title={challengeInfo.challenge.feedbackText}
          >
            {challengeInfo.challenge.feedbackText}
          </div>
        </div>
      )
    },
    userFeedback: (challengeInfo) => {
      const userFeedback = challengeInfo.challenge.userFeedback
      if (!userFeedback) {
        return (
          <span className="text-sm text-muted-foreground">
            {t('common.notApplicable')}
          </span>
        )
      }

      const ratingValue = Number(userFeedback.overallRating)
      const roundedRating = Math.round(ratingValue)
      const hasValidRating = Number.isFinite(ratingValue) && roundedRating >= 1 && roundedRating <= 10

      if (!hasValidRating) {
        return (
          <span className="text-sm text-muted-foreground">
            {t('common.notApplicable')}
          </span>
        )
      }

      return (
        <div
          className="flex items-center gap-1"
          title={userFeedback.freeText?.trim() || undefined}
          aria-label={`User rating: ${roundedRating} out of 6`}
        >
          {Array.from({ length: roundedRating }, (_, index) => (
            <StarIconSolid key={index} className="h-4 w-4 text-warning" aria-hidden="true" />
          ))}
        </div>
      )
    },
  }

  const renderCellContent = (challengeInfo: ChallengeService.ChallengeInfo, columnKey: ChallengeColumnKey) => {
    const renderer = cellRenderers[columnKey]
    return renderer ? renderer(challengeInfo) : null
  }

  return (
    <div className="bg-card shadow overflow-hidden rounded-md sm:rounded-md border border-border">
      <div className="px-4 py-5 sm:px-6 flex items-center justify-between gap-4">
        <div>
          <h3 className="text-lg leading-6 font-medium text-foreground">
            {t('challenges.allChallenges')}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {loadingMode ? t('common.loading') : t('common.XRecords', { count: challenges.length })}
          </p>
        </div>
        {tableSettings && (
          <div className="flex items-center gap-2">
            <FilterSetManager
              tableId={tableSettings.tableId}
              sortColumn={tableSettings.sortColumn}
              sortDirection={tableSettings.sortDirection}
              filters={tableSettings.filters}
              visibleColumns={tableSettings.visibleColumns}
              defaultVisibleColumns={tableSettings.defaultVisibleColumns}
              filterLabels={tableSettings.filterLabels}
              setSort={tableSettings.setSort}
              clearColumnFilter={tableSettings.clearColumnFilter}
              clearAllSettings={tableSettings.clearAllSettings}
              applyFilterSet={tableSettings.applyFilterSet}
              columns={tableSettings.availableColumns}
            />
            <ColumnVisibilityDropdown
              availableColumns={tableSettings.availableColumns}
              isColumnVisible={tableSettings.isColumnVisible}
              toggleColumnVisibility={tableSettings.toggleColumnVisibility}
            />
          </div>
        )}
      </div>

      <div className="overflow-x-auto overflow-y-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-secondary/5">
            <tr>
              {visibleColumns.map((column) => {
                const filterKey = column.filterKey ?? column.key
                const isEnumFilter = column.filterType === 'enum'
                const isTextFilter = column.filterType === 'text'
                const isAsyncSearchFilter = column.filterType === 'async-search'
                const filterValue = filterApi && isTextFilter ? filterApi.getColumnFilter(filterKey) : ''
                const filterArray = filterApi && (isEnumFilter || isAsyncSearchFilter) ? filterApi.getColumnFilterArray(filterKey) : []
                const hasFilter = isTextFilter ? !!filterValue?.trim() : (filterArray?.length ?? 0) > 0
                const enumOptions = isEnumFilter ? getEnumOptionsForColumn(filterKey) : undefined

                return (
                  <th
                    key={column.key}
                    className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider"
                  >
                    <div className="flex items-center gap-2">
                      {column.sortable ? (
                        <button
                          onClick={() => onSort(column.key as keyof ChallengeService.Challenge)}
                          className="group flex items-center gap-1.5 hover:text-foreground transition-colors"
                        >
                          <span>{t(column.label)}</span>
                          {renderSortIcon(
                            sortField === column.key ? sortDirection : null
                          )}
                        </button>
                      ) : (
                        <span>{t(column.label)}</span>
                      )}
                      {isEnumFilter && enumOptions && filterApi && (
                        <EnumFilterDropdown
                          label={column.label}
                          labelT={t}
                          enumOptions={enumOptions}
                          filterArray={filterArray}
                          hasFilter={hasFilter}
                          onToggleFilterItem={(value) =>
                            filterApi.toggleColumnFilterArrayItem(
                              filterKey,
                              value
                            )
                          }
                          onClearFilter={() =>
                            filterApi.clearColumnFilter(filterKey)
                          }
                        />
                      )}
                      {isTextFilter && filterApi && (
                        <TextFilterPopover
                          label={column.label}
                          labelT={t}
                          filterValue={filterValue}
                          hasFilter={hasFilter}
                          onFilterChange={(value) =>
                            filterApi.setColumnFilter(filterKey, value)
                          }
                          onClearFilter={() =>
                            filterApi.clearColumnFilter(filterKey)
                          }
                        />
                      )}
                      {isAsyncSearchFilter &&
                        filterApi &&
                        asyncSearchFetchers?.[filterKey] && (
                          <AsyncMultiPick
                            mode={filterKey === 'taskId' ? 'preload' : 'search'}
                            selected={filterArray}
                            onChange={(values) =>
                              filterApi.setColumnFilterArray(filterKey, values)
                            }
                            fetchOptions={asyncSearchFetchers[filterKey]}
                            labels={{
                              placeholder: t('common.search'),
                              searchPlaceholder: t('common.search'),
                            }}
                            listClassName="w-[360px]"
                            customTrigger={({ selected }) => (
                              <button
                                className={`flex items-center justify-center h-4 w-4 hover:text-foreground transition-colors ${selected.length > 0
                                  ? 'text-accent'
                                  : 'text-muted-foreground/50'
                                  }`}
                              >
                                <FunnelIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                          />
                        )}
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {loadingMode ? (
              <tr>
                <td colSpan={visibleColumns.length} className="px-6 py-12 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                    <span className="ms-2 text-sm text-muted-foreground">{t('common.loading')}</span>
                  </div>
                </td>
              </tr>
            ) : hasNoFilterResults ? (
              <tr>
                <td colSpan={visibleColumns.length} className="px-6 py-8">
                  <NoDataEmptyState
                    icon={FunnelIcon}
                    title={t('common.noFilterResults')}
                    description={t('common.noFilterResultsDescription')}
                    buttonText={onClearFilters ? t('common.resetFilters') : undefined}
                    onButtonClick={onClearFilters}
                  />
                </td>
              </tr>
            ) : (
              challenges.map((challengeInfo, index) => {
                const isLoadMoreSentinel = hasMore && !loadingMode && index === loadMoreSentinelIndex
                const slug = challengeInfo.challenge.slug

                return (
                  <tr
                    key={challengeInfo.challenge.id}
                    ref={isLoadMoreSentinel ? loadMoreSentinelRef : undefined}
                    onClick={() => {
                      if (slug) navigate({ to: '/admin/challenges/$slug', params: { slug } })
                    }}
                    className={slug ? 'cursor-pointer hover:bg-secondary/5' : undefined}
                  >
                    {visibleColumns.map((column) => (
                      <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                        {renderCellContent(challengeInfo, column.key)}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {hasMore && !loadingMode && (
        <div className="flex items-center justify-center py-4">
          {isPageLoading && (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent"></div>
              <span className="ms-2 text-sm text-muted-foreground">{t('common.loading')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
